import { useCallback, useMemo, useRef, useState, type ComponentType, type ReactNode } from "react";
import { ChevronDown, GripVertical, LayoutList, Lock, Minus, Plus, X } from "lucide-react";
import { useConfirm } from "@omit/react-confirm-dialog";
import { Dialog, DialogContent } from "@/modern/components/ui/dialog";
import { Button } from "@/modern/components/ui/button";
import { Badge } from "@/modern/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/modern/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { SwivuList } from "@/modern/components/swivu-list";
import { cn } from "@/modern/utils";
import { KanbanCardContent, type KanbanCardContentProps } from "@/modern/components/kanban/KanbanCardContent";
import {
  KANBAN_CARD_FIELDS_GUIDE_TARGETS,
  MODAL_GUIDE_Z_INDEX,
  PageGuideTrigger,
  guideTargetProps,
  replayPageGuide,
} from "@/react/guides";
import {
  addKanbanVisibleField,
  buildKanbanCardPreviewRecord,
  getAvailableKanbanFields,
  getCatalogFieldById,
  getSettingsPinnedKanbanFields,
  removeKanbanVisibleField,
  reorderKanbanVisibleField,
} from "@/modern/constants/kanbanFieldUtils";
import {
  KANBAN_CARD_FIELD_GROUP_LABELS,
  type KanbanCardFieldDefinition,
  type KanbanCardFieldGroup,
} from "@/modern/constants/kanbanCardFieldCatalog";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { ENTITY_TYPES, TASK_BUDGET_FIELDS, VIEW_TYPES } from "@/other/constants/OtherKeys";
import type { useSwivuKanban } from "@/react/hooks/useSwivuKanban";
import type { ViewType } from "@/react/guides";

const KANBAN_GUIDE_VIEW_TYPES: Partial<Record<string, ViewType>> = {
  [ENTITY_TYPES.TASK]: VIEW_TYPES.tasks,
  [ENTITY_TYPES.RESOURCE]: VIEW_TYPES.resources,
  [ENTITY_TYPES.CLIENT]: VIEW_TYPES.clients,
};

const NESTED_EDITOR_CONFIRM_DIALOG_PROPS = {
  alertDialogOverlay: { className: "z-[calc(var(--swivu-bryntum-z-index)+10)]" },
  alertDialogContent: { className: "z-[calc(var(--swivu-bryntum-z-index)+10)]" },
} as const;

const GROUP_ORDER: KanbanCardFieldGroup[] = ["budget", "scheduling", "metadata", "resource", "audit"];

const FIELD_GROUP_BADGE_CLASS: Record<KanbanCardFieldGroup, string> = {
  budget: "bg-emerald-500/12 text-emerald-700 dark:text-emerald-400",
  scheduling: "bg-sky-500/12 text-sky-700 dark:text-sky-400",
  metadata: "bg-violet-500/12 text-violet-700 dark:text-violet-400",
  resource: "bg-amber-500/12 text-amber-700 dark:text-amber-400",
  audit: "bg-slate-500/12 text-slate-700 dark:text-slate-400",
};

function FieldGroupBadge({ group }: { group: KanbanCardFieldGroup }) {
  return (
    <span
      className={cn(
        "mb-2.5 inline-flex items-center rounded-full px-3 py-1 text-base font-medium leading-none",
        FIELD_GROUP_BADGE_CLASS[group]
      )}
    >
      {KANBAN_CARD_FIELD_GROUP_LABELS[group]}
    </span>
  );
}

function VisibleSlotBadge({ count, max }: { count: number; max: number }) {
  const isFull = count >= max;
  return (
    <Badge
      variant={isFull ? "default" : "secondary"}
      size="sm"
      className="!px-1.5 !py-0.5 !text-xs font-medium tabular-nums"
    >
      {count} / {max}
    </Badge>
  );
}

export type KanbanCardFieldsEditorRecord = {
  kanbanRef: React.MutableRefObject<ReturnType<typeof useSwivuKanban> | null>;
  sampleRecord?: any;
};

type KanbanCardFieldsEditorProps = {
  record?: KanbanCardFieldsEditorRecord | null;
  onClose: () => void;
  isShow?: boolean;
};

type VisibleListItem = KanbanCardFieldDefinition & { id: string };

const fieldIdsEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id, index) => id === b[index]);

function EditorHeader({
  title,
  actions,
  onClose,
  onShowGuide,
}: {
  title: string;
  actions: ReactNode;
  onClose: () => void;
  onShowGuide?: () => void;
}) {
  return (
    <div className="flex min-w-0 flex-none items-center justify-between gap-3 border-b px-5 py-4">
      <div className="flex flex-1 items-center gap-2 overflow-hidden">
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        {onShowGuide ? (
          <PageGuideTrigger onClick={onShowGuide} variant="inline" showTooltip={false} />
        ) : null}
      </div>
      <div className="flex flex-none items-center gap-2" {...guideTargetProps(KANBAN_CARD_FIELDS_GUIDE_TARGETS.actions)}>
        {actions}
        <Button type="button" variant="ghost" size="icon-sm" className="gap-2" onClick={onClose}>
          <X />
          <span className="sr-only">Close</span>
        </Button>
      </div>
    </div>
  );
}

function SettingsSection({
  title,
  meta,
  children,
  first = false,
  guideTarget,
}: {
  title: string;
  meta?: ReactNode;
  children: ReactNode;
  first?: boolean;
  guideTarget?: string;
}) {
  return (
    <section
      className={cn(!first && "border-t border-border/50 pt-5")}
      {...(guideTarget ? guideTargetProps(guideTarget) : {})}
    >
      <div className="mb-3 flex min-h-8 items-center gap-2">
        <h3 className="text-base font-medium text-foreground">{title}</h3>
        {meta}
      </div>
      {children}
    </section>
  );
}

function FieldRowIcon({
  icon: Icon,
  variant = "available",
}: {
  icon: ComponentType<{ className?: string }>;
  variant?: "available" | "visible";
}) {
  return (
    <div
      className={cn(
        "flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground",
        variant === "visible" ? "bg-muted" : "bg-muted/60"
      )}
    >
      <Icon className="size-4" aria-hidden />
    </div>
  );
}

const fieldRowClass =
  "flex w-full items-center gap-3 rounded-lg border border-transparent px-3 py-2.5 text-left transition-colors hover:border-border/60 hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

const visibleFieldRowClass = cn(
  "flex w-full items-center gap-3 rounded-lg border border-border/50 bg-card px-3 py-2.5 shadow-sm transition-colors hover:border-border",
  "!gap-3 !border-border/50 !bg-card !p-0 !px-3 !py-2.5 !shadow-sm hover:!border-border hover:!bg-card"
);

const fieldRowActionClass =
  "shrink-0 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm";

function PinnedFieldRow({ field }: { field: KanbanCardFieldDefinition }) {
  const Icon = field.icon ?? LayoutList;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            visibleFieldRowClass,
            "cursor-not-allowed select-none border-dashed bg-muted/30 text-muted-foreground"
          )}
          aria-disabled="true"
        >
          <span className="flex size-8 shrink-0 items-center justify-center" aria-hidden>
            <GripVertical className="size-4 opacity-0" />
          </span>
          <FieldRowIcon icon={Icon} variant="visible" />
          <span className="min-w-0 flex-1 truncate text-base font-medium">{field.label}</span>
          <Lock className="size-4 shrink-0 text-muted-foreground/80" aria-hidden />
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[14rem] text-center">
        Always shown on the card and cannot be removed or reordered.
      </TooltipContent>
    </Tooltip>
  );
}

function KanbanBoardCardPreview({
  entityType,
  visibleFieldIds,
  grouperField,
  project,
  userPreferences,
}: Pick<KanbanCardContentProps, "entityType" | "visibleFieldIds" | "grouperField" | "project" | "userPreferences">) {
  const previewRecord = useMemo(() => buildKanbanCardPreviewRecord(entityType), [entityType]);

  return (
    <div className="relative flex w-full max-w-[18rem] items-start gap-2 overflow-hidden rounded-sm border border-border bg-background p-4 shadow-xs dark:bg-background/80">
      <span
        className="pointer-events-none flex shrink-0 items-center text-muted-foreground opacity-0"
        aria-hidden
      >
        <GripVertical className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <KanbanCardContent
          key={visibleFieldIds.join("|")}
          entityType={entityType}
          record={previewRecord}
          visibleFieldIds={visibleFieldIds}
          grouperField={grouperField}
          project={project}
          userPreferences={userPreferences}
        />
      </div>
    </div>
  );
}

function AvailableFieldRow({
  field,
  isAtMax,
  onAdd,
  onAtMaxAttempt,
}: {
  field: KanbanCardFieldDefinition;
  isAtMax: boolean;
  onAdd: (fieldId: string) => void;
  onAtMaxAttempt: () => void;
}) {
  const Icon = field.icon ?? LayoutList;

  return (
    <button
      type="button"
      onClick={() => {
        if (isAtMax) onAtMaxAttempt();
        else onAdd(field.id);
      }}
      className={fieldRowClass}
    >
      <FieldRowIcon icon={Icon} />
      <span className="min-w-0 flex-1 truncate text-base text-foreground">{field.label}</span>
      <Plus className="size-4 shrink-0 text-muted-foreground" aria-hidden />
    </button>
  );
}

export function KanbanCardFieldsEditor({ record, onClose, isShow = true }: KanbanCardFieldsEditorProps) {
  const kanban = record?.kanbanRef?.current;
  const entityType = kanban?.entityType ?? "";
  const savedFieldIdsRef = useRef(kanban?.visibleFieldIds ?? []);
  const isConfirmDialogOpenRef = useRef(false);
  const { project, userPreferences } = useSwivuBryntumContext();
  const confirm = useConfirm();

  const showEditorConfirm = useCallback(
    async (options: Parameters<typeof confirm>[0]) => {
      isConfirmDialogOpenRef.current = true;
      try {
        return await confirm(options);
      } finally {
        window.setTimeout(() => {
          isConfirmDialogOpenRef.current = false;
        }, 0);
      }
    },
    [confirm]
  );

  const guideViewType = KANBAN_GUIDE_VIEW_TYPES[entityType];
  const replayGuide = useCallback(() => {
    if (!userPreferences || !guideViewType) return;
    void replayPageGuide(guideViewType, userPreferences, MODAL_GUIDE_Z_INDEX);
  }, [guideViewType, userPreferences]);

  const [draftFieldIds, setDraftFieldIds] = useState<string[]>(() => savedFieldIdsRef.current);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);

  const catalog = kanban?.catalog ?? [];
  const defaults = kanban?.defaults ?? [];
  const maxFields = kanban?.maxFields ?? 4;

  const isDirty = useMemo(
    () => !fieldIdsEqual(draftFieldIds, kanban?.visibleFieldIds ?? savedFieldIdsRef.current),
    [draftFieldIds, kanban?.visibleFieldIds]
  );

  const availableFields = useMemo(
    () => getAvailableKanbanFields(catalog, draftFieldIds),
    [catalog, draftFieldIds]
  );
  const isAtMax = draftFieldIds.length >= maxFields;

  const pinnedFields = useMemo(() => getSettingsPinnedKanbanFields(catalog), [catalog]);

  const visibleItems: VisibleListItem[] = useMemo(
    () =>
      draftFieldIds
        .map((id) => {
          const field = getCatalogFieldById(entityType, id);
          return field ? { ...field, id: field.id } : null;
        })
        .filter((f): f is VisibleListItem => f != null),
    [entityType, draftFieldIds]
  );

  const previewUserPreferences = useMemo(() => {
    if (!userPreferences) return null;
    return {
      getSetting: (key: string, defaultValue?: string, isBoardPref?: boolean) => {
        if (key === PERSISTENCE_STATE_KEYS.budgetLabel) {
          const actual = userPreferences.getSetting(key, defaultValue, isBoardPref);
          if (!actual || actual === "notDisplayed") {
            return TASK_BUDGET_FIELDS.totalCostEstimated;
          }
          return actual;
        }
        return userPreferences.getSetting(key, defaultValue, isBoardPref);
      },
    };
  }, [userPreferences]);

  const groupedAvailable = useMemo(() => {
    const groups = new Map<KanbanCardFieldGroup, KanbanCardFieldDefinition[]>();
    for (const field of availableFields) {
      const list = groups.get(field.group) ?? [];
      list.push(field);
      groups.set(field.group, list);
    }
    return GROUP_ORDER.filter((g) => groups.has(g)).map((g) => ({ group: g, fields: groups.get(g)! }));
  }, [availableFields]);

  const handleReorder = (movedItem: VisibleListItem, beforeItem: VisibleListItem | null) => {
    setDraftFieldIds((current) => reorderKanbanVisibleField(current, movedItem.id, beforeItem?.id ?? null));
  };

  const persistDraft = () => {
    kanban?.saveVisibleFieldIds(draftFieldIds);
  };

  const handleSave = () => {
    persistDraft();
    onClose();
  };

  const handleAtMaxAttempt = async () => {
    await showEditorConfirm({
      title: "Maximum fields reached",
      description: `You can show up to ${maxFields} optional fields on each card. Remove a field from "Visible on card" first, then you can add another.`,
      confirmText: "OK",
      cancelButton: null,
      ...NESTED_EDITOR_CONFIRM_DIALOG_PROPS,
    });
  };

  const handleClose = async () => {
    if (isDirty) {
      const confirmed = await showEditorConfirm({
        title: "Unsaved Changes",
        description: "You have unsaved changes. What would you like to do?",
        ...NESTED_EDITOR_CONFIRM_DIALOG_PROPS,
        customActions: ({ confirm: confirmClose, cancel }: { confirm: () => void; cancel: () => void }) => (
          <div className="flex w-full justify-end gap-3">
            <Button variant="ghost" onClick={cancel} size="sm">
              Keep working
            </Button>
            <Button variant="destructive" onClick={confirmClose} size="sm">
              Do not save
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={() => {
                persistDraft();
                confirmClose();
              }}
            >
              Save & close
            </Button>
          </div>
        ),
      });
      if (confirmed) onClose();
      return;
    }
    onClose();
  };

  if (!isShow || !entityType || !kanban) return null;

  const previewProps = {
    entityType,
    visibleFieldIds: draftFieldIds,
    grouperField: kanban.activeConfig?.grouperField,
    project,
    userPreferences: previewUserPreferences,
  };

  const headerActions = (
    <>
      <Button type="button" variant="ghost" size="sm" onClick={() => setDraftFieldIds(defaults)}>
        Reset to default
      </Button>
      <Button type="button" size="sm" onClick={handleSave} disabled={!isDirty}>
        Save changes
      </Button>
    </>
  );

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isConfirmDialogOpenRef.current) {
          void handleClose();
        }
      }}
    >
      <DialogContent
        className="z-[--swivu-bryntum-z-index] flex max-h-[min(90vh,44rem)] max-w-[min(56rem,96vw)] flex-col gap-0 overflow-hidden border-border/60 bg-background p-0"
        showCloseButton={false}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <EditorHeader title="Card fields" actions={headerActions} onClose={handleClose} onShowGuide={replayGuide} />

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-5">
              <div className="space-y-5">
                <SettingsSection
                  title="Visible on card"
                  meta={
                    <VisibleSlotBadge
                      count={pinnedFields.length + draftFieldIds.length}
                      max={pinnedFields.length + maxFields}
                    />
                  }
                  first
                  guideTarget={KANBAN_CARD_FIELDS_GUIDE_TARGETS.visibleSection}
                >
                  <div className="space-y-2">
                    {pinnedFields.map((field) => (
                      <PinnedFieldRow key={field.id} field={field} />
                    ))}
                    {visibleItems.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-border/70 px-4 py-8 text-center">
                        <p className="text-base text-muted-foreground">No optional fields selected</p>
                      </div>
                    ) : (
                      <SwivuList.Root
                        key={draftFieldIds.join("|")}
                        items={visibleItems}
                        onReorder={handleReorder}
                        className="!space-y-2"
                      >
                        <SwivuList.Content emptyMessage="" className="!space-y-2">
                          {(item: VisibleListItem) => {
                            const Icon = item.icon ?? LayoutList;
                            return (
                              <SwivuList.Item value={item.id} className={visibleFieldRowClass}>
                                <SwivuList.ItemHandle className="!size-8 !min-h-0 !p-0 !text-muted-foreground/50" />
                                <FieldRowIcon icon={Icon} variant="visible" />
                                <span className="min-w-0 flex-1 truncate text-base font-medium text-foreground">
                                  {item.label}
                                </span>
                                <button
                                  type="button"
                                  className={fieldRowActionClass}
                                  onClick={() =>
                                    setDraftFieldIds((current) => removeKanbanVisibleField(current, item.id))
                                  }
                                  aria-label={`Remove ${item.label}`}
                                >
                                  <Minus className="size-4" aria-hidden />
                                </button>
                              </SwivuList.Item>
                            );
                          }}
                        </SwivuList.Content>
                      </SwivuList.Root>
                    )}
                  </div>
                </SettingsSection>

                <SettingsSection
                  title="Available fields"
                  guideTarget={KANBAN_CARD_FIELDS_GUIDE_TARGETS.availableSection}
                >
                  {availableFields.length === 0 ? (
                    <p className="py-5 text-center text-base text-muted-foreground">
                      All available fields are on the card.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {groupedAvailable.map(({ group, fields }) => (
                        <div key={group}>
                          <FieldGroupBadge group={group} />
                          <div className="space-y-1">
                            {fields.map((field) => (
                              <AvailableFieldRow
                                key={field.id}
                                field={field}
                                isAtMax={isAtMax}
                                onAdd={(fieldId) =>
                                  setDraftFieldIds((current) =>
                                    addKanbanVisibleField(current, fieldId, maxFields)
                                  )
                                }
                                onAtMaxAttempt={handleAtMaxAttempt}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </SettingsSection>

                <Collapsible open={mobilePreviewOpen} onOpenChange={setMobilePreviewOpen} className="lg:hidden">
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      aria-expanded={mobilePreviewOpen}
                      className="flex w-full items-center justify-between border-t border-border/50 pt-5 text-base font-medium text-muted-foreground"
                    >
                      Preview
                      <ChevronDown
                        className={cn("size-4 transition-transform", mobilePreviewOpen && "rotate-180")}
                      />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="flex justify-center pt-5">
                    <KanbanBoardCardPreview {...previewProps} />
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>
          </div>

          <div
            className="hidden min-h-0 min-w-0 flex-1 items-center justify-center border-l border-border/50 bg-muted/10 p-8 lg:flex"
            {...guideTargetProps(KANBAN_CARD_FIELDS_GUIDE_TARGETS.preview)}
          >
            <KanbanBoardCardPreview {...previewProps} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
